/**
 * Фейковый поток снимков боя — для разработки рендера (C1), HUD (C3), окон (C4) и звука (C7), пока нет настоящего
 * ядра (E1). Правдоподобно, но без баланса: зомби идут к штабу и бьют его, герои и пушка стреляют, зомби умирают
 * и роняют монеты, волны сменяются, каждая 5-я — с боссом. Детерминирован по сиду: один сид — одни и те же снимки.
 *
 * Это не симуляция для сервера и не образец для E1: реализация упрощённая и будет заменена `src/sim/`.
 */
import type {
  BattleEvent,
  BattleSnapshot,
  CoinDropState,
  Direction,
  EffectKind,
  EffectState,
  HeroId,
  HqLook,
  PlatformState,
  PlayerCommand,
  ProjectileKind,
  Vec2,
  WavePhase,
  ZombieKind,
} from "@newfungame/contracts";
import { DEFAULT_FIELD, FIXED_ONE, TICK_RATE } from "@newfungame/contracts";
import { createFakeRng } from "./rng";

/** Параметры фейкового потока. */
export interface FakeBattleOptions {
  /** С какой волны начать (по умолчанию 1). Например, 12 — уже три героя и «Автонавыки». */
  startWave?: number;
  /** Монеты в кошельке на старте. */
  coins?: number;
  /** Навыки сами срабатывают при готовности (по умолчанию да — чтобы на экране было движение). */
  autoCast?: boolean;
}

/** Поток снимков: каждый `step()` — один тик (1/30 с). */
export interface FakeBattleStream {
  /** Продвинуть бой на тик и вернуть новый снимок. */
  step(): BattleSnapshot;
  /** Последний снимок (до первого `step()` — начальный). */
  current(): BattleSnapshot;
  /** Команда игрока; применится на следующем `step()`. */
  command(cmd: PlayerCommand): void;
}

const HERO_ORDER: readonly HeroId[] = ["gunner", "bomber", "sparky", "blaze", "frosty"];
const UNLOCK_WAVES = [1, 5, 10, 20, 30, 50] as const;
const PLATFORM_POS: readonly Vec2[] = [
  { x: -1500, y: 2200 },
  { x: 1500, y: 2200 },
  { x: -3200, y: 1500 },
  { x: 3200, y: 1500 },
  { x: -3300, y: 3100 },
  { x: 3300, y: 3100 },
];
const HQ_POS: Vec2 = { x: 0, y: 900 };
const HQ_HP = 1000;
/** Зомби останавливается и бьёт штаб на этой линии. */
const HQ_FRONT_Y = 1900;
const BOSS_IDS = ["boss_conehead", "boss_butcher", "boss_broodmother"] as const; // порядок как в data/waves.json (bossOrder)

interface HeroTune {
  projectile: ProjectileKind;
  cooldown: number;
  damage: number;
  speed: number;
  splash: number;
  hitEffect: EffectKind;
}
const HERO_TUNE: Record<HeroId, HeroTune> = {
  gunner: { projectile: "bullet", cooldown: 12, damage: 9, speed: 520, splash: 0, hitEffect: "hit" },
  bomber: { projectile: "bomb", cooldown: 40, damage: 14, speed: 260, splash: 1200, hitEffect: "explosion" },
  sparky: { projectile: "spark", cooldown: 28, damage: 16, speed: 0, splash: 0, hitEffect: "lightning" },
  blaze: { projectile: "flame", cooldown: 20, damage: 7, speed: 300, splash: 700, hitEffect: "fire" },
  frosty: { projectile: "frost", cooldown: 30, damage: 8, speed: 360, splash: 600, hitEffect: "ice" },
  radiation: { projectile: "bomb", cooldown: 40, damage: 25, speed: 300, splash: 1600, hitEffect: "explosion" },
  cryo: { projectile: "frost", cooldown: 28, damage: 14, speed: 360, splash: 1400, hitEffect: "ice" },
  laser: { projectile: "spark", cooldown: 24, damage: 20, speed: 900, splash: 600, hitEffect: "hit" },
  plague: { projectile: "bomb", cooldown: 30, damage: 22, speed: 300, splash: 1200, hitEffect: "explosion" },
};

interface ZombieTune {
  hp: number;
  speed: number;
  damage: number;
  coins: number;
  shield: number;
}
const ZOMBIE_TUNE: Record<ZombieKind | "boss", ZombieTune> = {
  walker: { hp: 30, speed: 24, damage: 6, coins: 1, shield: 0 },
  runner: { hp: 16, speed: 46, damage: 3, coins: 1, shield: 0 },
  brute: { hp: 90, speed: 14, damage: 14, coins: 3, shield: 0 },
  shield: { hp: 36, speed: 20, damage: 6, coins: 2, shield: 30 },
  boss: { hp: 700, speed: 11, damage: 40, coins: 40, shield: 0 },
};

const DIE_TICKS = 18;
const COIN_FLIGHT = 20;
const SKILL_COOLDOWN = 10 * TICK_RATE;
const SKILL_ACTIVE = 20;
const INTRO_TICKS = 45;
const OUTRO_TICKS = 60;

interface Zombie {
  id: number;
  kind: ZombieKind | "boss";
  bossId: string | null;
  pos: Vec2;
  facing: Direction;
  anim: "walk" | "attack" | "die";
  animTick: number;
  hp: number;
  hpMax: number;
  shield: number;
  attackCd: number;
  frozen: number;
  burning: number;
}
interface Hero {
  id: number;
  heroId: HeroId;
  slot: number;
  pos: Vec2;
  facing: Direction;
  anim: "idle" | "shoot" | "skill";
  animTick: number;
  cd: number;
  skillCd: number;
  skillActive: number;
  targetId: number | null;
}
interface Projectile {
  id: number;
  kind: ProjectileKind;
  pos: Vec2;
  from: Vec2;
  to: Vec2;
  targetId: number;
  owner: HeroId | "hq";
  damage: number;
  speed: number;
  splash: number;
  hitEffect: EffectKind;
}

/** Целочисленный корень (IEEE sqrt корректно округляется — результат одинаков на всех платформах). */
function isqrt(n: number): number {
  return Math.floor(Math.sqrt(n));
}

function dist2(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

/** Направление по вектору (8 секторов, tg 22,5° ≈ 0,414) — без тригонометрии. */
export function directionOf(dx: number, dy: number): Direction {
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  if (ax === 0 && ay === 0) return "s";
  if (ay * 1000 <= ax * 414) return dx > 0 ? "e" : "w";
  if (ax * 1000 <= ay * 414) return dy > 0 ? "n" : "s";
  if (dy > 0) return dx > 0 ? "ne" : "nw";
  return dx > 0 ? "se" : "sw";
}

function hqLook(hp: number): HqLook {
  if (hp <= 0) return "destroyed";
  if (hp * 3 > HQ_HP * 2) return "intact";
  if (hp * 3 > HQ_HP) return "damaged";
  return "critical";
}

/**
 * Создать фейковый поток боя.
 *
 * ```ts
 * const stream = createFakeBattleStream(42);
 * app.ticker.add(() => render(stream.step())); // упрощённо; честно — шаг 30 Гц с накоплением времени
 * ```
 */
export function createFakeBattleStream(seed: number, options: FakeBattleOptions = {}): FakeBattleStream {
  const rng = createFakeRng(seed);
  const autoCast = options.autoCast ?? true;
  let nextId = 1;
  let frame = 0;
  let waveNo = Math.max(1, Math.trunc(options.startWave ?? 1));
  let phase: WavePhase = "intro";
  let waveTick = 0;
  let total = 0;
  let spawned = 0;
  let killed = 0;
  let spawnEvery = 1;
  let phaseLeft = INTRO_TICKS;
  let hqHp = HQ_HP;
  let cannonCd = 0;
  let cannonFiring = false;
  let cannonFacing: Direction = "n";
  let cannonTarget: number | null = null;
  let coins = Math.max(0, Math.trunc(options.coins ?? 120));
  let earned = 0;
  let autoEnabled = autoCast;
  let events: BattleEvent[] = [];
  let pending: PlayerCommand[] = [];
  let zombies: Zombie[] = [];
  let projectiles: Projectile[] = [];
  let effects: EffectState[] = [];
  let drops: CoinDropState[] = [];
  let heroes: Hero[] = [];
  let snapshot: BattleSnapshot;

  const isBoss = () => waveNo % 5 === 0;

  function syncHeroes(announce: boolean): void {
    for (let i = 0; i < HERO_ORDER.length; i++) {
      const heroId = HERO_ORDER[i]!;
      if (waveNo < UNLOCK_WAVES[i]! || heroes.some((h) => h.heroId === heroId)) continue;
      heroes.push({
        id: nextId++,
        heroId,
        slot: i,
        pos: PLATFORM_POS[i]!,
        facing: "n",
        anim: "idle",
        animTick: 0,
        cd: rng.int(0, HERO_TUNE[heroId].cooldown),
        skillCd: rng.int(TICK_RATE, SKILL_COOLDOWN),
        skillActive: 0,
        targetId: null,
      });
      if (announce) events.push({ type: "heroUnlocked", heroId, slot: i });
    }
    heroes = heroes.filter((h) => UNLOCK_WAVES[h.slot]! <= waveNo);
  }

  function startWave(): void {
    phase = "intro";
    phaseLeft = INTRO_TICKS;
    waveTick = 0;
    total = 10 + 3 * (waveNo - 1) + (isBoss() ? 1 : 0);
    spawned = 0;
    killed = 0;
    spawnEvery = Math.max(6, Math.trunc((20 * TICK_RATE) / total));
    hqHp = HQ_HP;
    earned = 0;
    zombies = [];
    projectiles = [];
    effects = [];
    drops = [];
    syncHeroes(frame > 0);
    events.push({ type: "waveStarted", wave: waveNo, isBoss: isBoss() });
  }

  function addEffect(kind: EffectKind, pos: Vec2, durationTicks: number, value = 0, to: Vec2 | null = null): void {
    effects.push({ id: nextId++, kind, pos, to, startTick: waveTick, durationTicks, value });
  }

  function spawnZombie(): void {
    const lastIsBoss = isBoss() && spawned === total - 1;
    let kind: ZombieKind | "boss" = "walker";
    if (lastIsBoss) kind = "boss";
    else {
      const r = rng.int(0, 99);
      if (waveNo >= 6 && r < 15) kind = "shield";
      else if (waveNo >= 4 && r < 30) kind = "brute";
      else if (waveNo >= 2 && r < 55) kind = "runner";
    }
    const t = ZOMBIE_TUNE[kind];
    const hpMax = t.hp + Math.trunc((t.hp * (waveNo - 1) * 120) / 1000);
    const bossId = kind === "boss" ? BOSS_IDS[(Math.trunc(waveNo / 5) - 1) % BOSS_IDS.length]! : null;
    const z: Zombie = {
      id: nextId++,
      kind,
      bossId,
      pos: { x: kind === "boss" ? 0 : rng.int(-4000, 4000), y: DEFAULT_FIELD.depth - rng.int(0, 600) },
      facing: "s",
      anim: "walk",
      animTick: 0,
      hp: hpMax,
      hpMax,
      shield: t.shield,
      attackCd: rng.int(0, TICK_RATE),
      frozen: 0,
      burning: 0,
    };
    zombies.push(z);
    spawned++;
    events.push({ type: "zombieSpawned", id: z.id, kind });
    if (bossId) events.push({ type: "bossSpawned", id: z.id, bossId });
  }

  function alive(): Zombie[] {
    return zombies.filter((z) => z.anim !== "die");
  }

  function nearest(from: Vec2, range2: number): Zombie | null {
    let best: Zombie | null = null;
    let bestD = range2;
    for (const z of zombies) {
      if (z.anim === "die") continue;
      const d = dist2(from, z.pos);
      if (d <= bestD) {
        best = z;
        bestD = d;
      }
    }
    return best;
  }

  function damage(z: Zombie, amount: number, crit: boolean): void {
    if (z.anim === "die") return;
    let left = amount;
    if (z.shield > 0) {
      const absorbed = Math.min(z.shield, left);
      z.shield -= absorbed;
      left -= absorbed;
    }
    z.hp = Math.max(0, z.hp - left);
    events.push({ type: "hit", targetId: z.id, damage: amount, crit });
    addEffect(crit ? "critNumber" : "damageNumber", { x: z.pos.x, y: z.pos.y + 300 }, 24, amount);
    if (z.hp > 0) return;
    z.anim = "die";
    z.animTick = 0;
    killed++;
    events.push({ type: "zombieKilled", id: z.id, kind: z.kind });
    if (z.kind === "boss" && z.bossId) events.push({ type: "bossKilled", id: z.id, bossId: z.bossId });
    const value = ZOMBIE_TUNE[z.kind].coins + Math.trunc(waveNo / 4);
    const coinId = nextId++;
    drops.push({ id: coinId, pos: { ...z.pos }, value, startTick: waveTick, flightTicks: COIN_FLIGHT });
    events.push({ type: "coinsDropped", coinId, value });
    addEffect("coinBurst", z.pos, 12);
  }

  function explode(at: Vec2, radius: number, amount: number, effect: EffectKind): void {
    addEffect(effect, at, 18, radius);
    const r2 = radius * radius;
    for (const z of alive()) if (dist2(at, z.pos) <= r2) damage(z, amount, false);
  }

  function fire(owner: HeroId | "hq", from: Vec2, target: Zombie, tune: HeroTune, dmg: number): void {
    const id = nextId++;
    events.push({ type: "shot", owner, projectileId: id });
    addEffect("muzzle", from, 6);
    if (tune.speed === 0) {
      addEffect(tune.hitEffect, from, 8, 0, { ...target.pos });
      damage(target, dmg, false);
      return;
    }
    projectiles.push({
      id,
      kind: tune.projectile,
      pos: { ...from },
      from: { ...from },
      to: { ...target.pos },
      targetId: target.id,
      owner,
      damage: dmg,
      speed: tune.speed,
      splash: tune.splash,
      hitEffect: tune.hitEffect,
    });
  }

  function castSkill(h: Hero, auto: boolean): void {
    if (h.skillCd > 0) return;
    h.skillCd = SKILL_COOLDOWN;
    h.skillActive = SKILL_ACTIVE;
    h.anim = "skill";
    h.animTick = 0;
    events.push({ type: "skillCast", heroId: h.heroId, skillId: `skill_${h.heroId}`, auto });
    const target = nearest(h.pos, (20 * FIXED_ONE) ** 2);
    if (target) explode(target.pos, 1800, 40 + waveNo * 4, HERO_TUNE[h.heroId].hitEffect);
  }

  function applyCommands(): void {
    for (const cmd of pending) {
      if (cmd.type === "castSkill") {
        const h = heroes.find((x) => x.heroId === cmd.heroId);
        if (h) castSkill(h, false);
      } else if (cmd.type === "setAutoSkills") {
        autoEnabled = cmd.enabled;
      }
      // chooseBoost: в фейке усилений нет — команда принимается и ничего не меняет
    }
    pending = [];
  }

  function stepFight(): void {
    if (spawned < total && waveTick % spawnEvery === 0) spawnZombie();

    // зомби
    for (const z of zombies) {
      z.animTick++;
      if (z.anim === "die") continue;
      if (z.burning > 0 && --z.burning % 10 === 0) {
        damage(z, 2, false);
        if (z.hp === 0) continue;
      }
      const speed = z.frozen > 0 ? Math.trunc(ZOMBIE_TUNE[z.kind].speed / 2) : ZOMBIE_TUNE[z.kind].speed;
      if (z.frozen > 0) z.frozen--;
      if (z.pos.y > HQ_FRONT_Y) {
        const dx = z.pos.x === 0 ? 0 : -Math.sign(z.pos.x) * Math.min(Math.abs(z.pos.x), Math.trunc(speed / 4));
        const dy = -Math.min(speed, z.pos.y - HQ_FRONT_Y);
        z.pos = { x: z.pos.x + dx, y: z.pos.y + dy };
        z.facing = directionOf(dx, dy);
        continue;
      }
      if (z.anim !== "attack") {
        z.anim = "attack";
        z.animTick = 0;
      }
      z.facing = directionOf(HQ_POS.x - z.pos.x, HQ_POS.y - z.pos.y);
      if (--z.attackCd <= 0) {
        z.attackCd = TICK_RATE;
        const dmg = ZOMBIE_TUNE[z.kind].damage;
        hqHp = Math.max(0, hqHp - dmg);
        events.push({ type: "hqDamaged", damage: dmg, hp: hqHp });
        addEffect("hqHit", { x: z.pos.x, y: HQ_FRONT_Y - 400 }, 10);
      }
    }
    zombies = zombies.filter((z) => !(z.anim === "die" && z.animTick >= DIE_TICKS));

    // герои
    for (const h of heroes) {
      h.animTick++;
      if (h.skillCd > 0 && --h.skillCd === 0) events.push({ type: "skillReady", heroId: h.heroId });
      if (h.skillActive > 0) h.skillActive--;
      if (h.anim !== "idle" && h.animTick >= 10) {
        h.anim = "idle";
        h.animTick = 0;
      }
      if (autoEnabled && h.skillCd === 0 && alive().length > 3) castSkill(h, true);
      const target = nearest(h.pos, (10 * FIXED_ONE) ** 2);
      h.targetId = target?.id ?? null;
      if (target) h.facing = directionOf(target.pos.x - h.pos.x, target.pos.y - h.pos.y);
      if (h.cd > 0) {
        h.cd--;
        continue;
      }
      if (!target) continue;
      const tune = HERO_TUNE[h.heroId];
      h.cd = tune.cooldown;
      if (h.anim === "idle") {
        h.anim = "shoot";
        h.animTick = 0;
      }
      const crit = rng.int(0, 99) < 10;
      fire(h.heroId, h.pos, target, tune, crit ? tune.damage * 2 : tune.damage);
    }

    // пушка штаба
    cannonFiring = false;
    const ct = nearest(HQ_POS, (7 * FIXED_ONE) ** 2);
    cannonTarget = ct?.id ?? null;
    if (ct) cannonFacing = directionOf(ct.pos.x - HQ_POS.x, ct.pos.y - HQ_POS.y);
    if (cannonCd > 0) cannonCd--;
    else if (ct) {
      cannonCd = 24;
      cannonFiring = true;
      fire("hq", { x: HQ_POS.x, y: HQ_POS.y + 500 }, ct, { ...HERO_TUNE.gunner, projectile: "cannonball" }, 12);
    }

    // снаряды
    const flying: Projectile[] = [];
    for (const p of projectiles) {
      const target = zombies.find((z) => z.id === p.targetId && z.anim !== "die");
      if (target) p.to = { ...target.pos };
      const dx = p.to.x - p.pos.x;
      const dy = p.to.y - p.pos.y;
      const d = isqrt(dx * dx + dy * dy);
      if (d > p.speed) {
        p.pos = { x: p.pos.x + Math.trunc((dx * p.speed) / d), y: p.pos.y + Math.trunc((dy * p.speed) / d) };
        flying.push(p);
        continue;
      }
      if (p.splash > 0) explode(p.to, p.splash, p.damage, p.hitEffect);
      else if (target) {
        addEffect(p.hitEffect, p.to, 8);
        damage(target, p.damage, false);
      }
      if (target && p.kind === "frost") target.frozen = 45;
      if (target && p.kind === "flame") target.burning = 40;
    }
    projectiles = flying;

    if (hqHp <= 0) {
      phase = "lost";
      phaseLeft = OUTRO_TICKS;
      events.push({ type: "waveLost", wave: waveNo, rollbackTo: Math.max(1, waveNo - 1) });
    } else if (spawned >= total && alive().length === 0) {
      phase = "cleared";
      phaseLeft = OUTRO_TICKS;
      events.push({ type: "waveCleared", wave: waveNo });
    }
  }

  function stepCommon(): void {
    // монеты долетают до счётчика
    const left: CoinDropState[] = [];
    for (const c of drops) {
      if (waveTick - c.startTick >= c.flightTicks) {
        coins += c.value;
        earned += c.value;
        events.push({ type: "coinsCollected", value: c.value });
      } else left.push(c);
    }
    drops = left;
    effects = effects.filter((e) => waveTick < e.startTick + e.durationTicks);
  }

  function build(): BattleSnapshot {
    const bossZ = zombies.find((z) => z.kind === "boss" && z.anim !== "die");
    const platforms: PlatformState[] = PLATFORM_POS.map((pos, slot) => ({
      slot,
      pos,
      unlocked: waveNo >= UNLOCK_WAVES[slot]!,
      unlockWave: UNLOCK_WAVES[slot]!,
      heroId: heroes.find((h) => h.slot === slot)?.heroId ?? null,
    }));
    return {
      frame,
      field: { ...DEFAULT_FIELD },
      wave: {
        number: waveNo,
        isBoss: isBoss(),
        phase,
        tick: waveTick,
        total,
        spawned,
        killed,
        progress: total === 0 ? 0 : Math.trunc((killed * 1000) / total),
        boss: bossZ
          ? { entityId: bossZ.id, bossId: bossZ.bossId ?? BOSS_IDS[0], hp: bossZ.hp, hpMax: bossZ.hpMax }
          : null,
      },
      hq: {
        pos: HQ_POS,
        hp: hqHp,
        hpMax: HQ_HP,
        look: hqLook(hqHp),
        cannon: { facing: cannonFacing, firing: cannonFiring, targetId: cannonTarget },
      },
      platforms,
      heroes: heroes.map((h) => ({
        id: h.id,
        heroId: h.heroId,
        slot: h.slot,
        pos: h.pos,
        facing: h.facing,
        anim: h.anim,
        animTick: h.animTick,
        level: 1 + Math.trunc(waveNo / 3),
        stars: 0,
        targetId: h.targetId,
        skill: {
          skillId: `skill_${h.heroId}`,
          cooldownLeft: h.skillCd,
          cooldownTotal: SKILL_COOLDOWN,
          activeLeft: h.skillActive,
        },
      })),
      zombies: zombies.map((z) => ({
        id: z.id,
        kind: z.kind,
        bossId: z.bossId,
        pos: { ...z.pos },
        facing: z.facing,
        anim: z.anim,
        animTick: z.animTick,
        hp: z.hp,
        hpMax: z.hpMax,
        shield: z.shield,
        statuses: [...(z.frozen > 0 ? (["frozen"] as const) : []), ...(z.burning > 0 ? (["burning"] as const) : [])],
      })),
      projectiles: projectiles.map((p) => ({
        id: p.id,
        kind: p.kind,
        pos: { ...p.pos },
        from: p.from,
        to: { ...p.to },
        owner: p.owner,
      })),
      effects: effects.map((e) => ({ ...e })),
      coins: drops.map((c) => ({ ...c })),
      parcels: [],
      parcelOpened: null,
      wallet: { coins, earnedThisWave: earned },
      autoSkills: { unlocked: waveNo >= 10, enabled: autoEnabled },
      events,
    };
  }

  startWave();
  snapshot = build();

  return {
    current: () => snapshot,
    command(cmd) {
      pending.push(cmd);
    },
    step() {
      frame++;
      events = [];
      applyCommands();
      if (phase === "intro") {
        if (--phaseLeft <= 0) phase = "fight";
      } else if (phase === "fight") {
        stepFight();
      } else if (--phaseLeft <= 0) {
        waveNo = phase === "cleared" ? waveNo + 1 : Math.max(1, waveNo - 1);
        startWave();
      }
      stepCommon();
      waveTick++;
      snapshot = build();
      return snapshot;
    },
  };
}
